import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, Lightbulb, Settings, Gift, AlertTriangle } from 'lucide-react';
import LawraLogo from '@/components/LawraLogo';
import AccountSettings from '@/components/AccountSettings';
import ReferralProgram from '@/components/ReferralProgram';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const SiteHeader = ({ user, trialUser, onLogout, onRequestFeature, onReportIssue, showAuthButtons = false }) => {
  const navigate = useNavigate();
  const [accountSettingsOpen, setAccountSettingsOpen] = useState(false);
  const [referralProgramOpen, setReferralProgramOpen] = useState(false);

  // Debug logging
  React.useEffect(() => {
    console.log('=== SiteHeader Debug ===');
    console.log('user:', user);
    console.log('trialUser:', trialUser);
    console.log('showAuthButtons:', showAuthButtons);
    console.log('user || trialUser:', (user || trialUser));
  }, [user, trialUser, showAuthButtons]);

  // Determine where the logo should link to
  const handleLogoClick = () => {
    if (user || trialUser) {
      navigate('/dashboard');
    } else {
      navigate('/');
    }
  };

  return (
    <div className="dashboard-header">
      <div className="header-content">
        <div className="header-left" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
          <img src="/lawra-header-logo.png" alt="Lawra" className="header-logo-image" />
        </div>
        
        <div className="header-right">
          {/* Mobile-only logo */}
          <div onClick={handleLogoClick} style={{ cursor: 'pointer', display: 'contents' }}>
            <img src="/lawra-header-logo.png" alt="Lawra" className="header-logo-image mobile-only" />
          </div>
          
          {/* Show user info if logged in */}
          {(user || trialUser) && (
            <>
              <span className="user-name">
                Hello, <span className="gold-text">{trialUser?.name || user?.full_name}</span>
              </span>
              {trialUser && (
                <span className="trial-badge">Trial Mode</span>
              )}
              
              <Button 
                data-testid="logout-btn"
                onClick={onLogout}
                style={{
                  background: 'linear-gradient(135deg, #D4AF37 0%, #E5B200 100%)',
                  color: '#1a1a1a',
                  border: 'none',
                  fontWeight: '700',
                  padding: '0.75rem 2rem',
                  borderRadius: '50px',
                  boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)',
                  animation: 'signupPulse 2s ease-in-out infinite',
                  whiteSpace: 'nowrap',
                  minWidth: '100px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #FFCE45 0%, #E5B200 100%)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(212, 175, 55, 0.8)';
                  e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
                  e.currentTarget.style.animation = 'none';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #D4AF37 0%, #E5B200 100%)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(212, 175, 55, 0.4)';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.animation = 'signupPulse 2s ease-in-out infinite';
                }}
              >
                {trialUser ? 'Sign Up' : 'Logout'}
              </Button>

              {/* Menu Dropdown - Now on far right */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="menu-button">
                    <Menu size={20} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="menu-dropdown">
                  <DropdownMenuItem onClick={() => setAccountSettingsOpen(true)}>
                    <Settings size={16} className="mr-2" />
                    Account Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setReferralProgramOpen(true)}>
                    <Gift size={16} className="mr-2" />
                    Referral Program
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onRequestFeature}>
                    <Lightbulb size={16} className="mr-2" />
                    Request Feature
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onReportIssue}>
                    <AlertTriangle size={16} className="mr-2" />
                    Report Issue
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}

          {/* Show auth buttons if not logged in */}
          {showAuthButtons && !user && !trialUser && (
            <Button 
              className="sign-in-btn"
              onClick={() => navigate('/auth')}
            >
              Sign In
            </Button>
          )}
        </div>
      </div>

      {/* Account Settings Modal */}
      <AccountSettings 
        open={accountSettingsOpen} 
        onOpenChange={setAccountSettingsOpen}
        user={user}
        trialUser={trialUser}
      />

      {/* Referral Program Modal */}
      <ReferralProgram 
        open={referralProgramOpen} 
        onOpenChange={setReferralProgramOpen}
        user={user}
        trialUser={trialUser}
      />
    </div>
  );
};

export default SiteHeader;
